/**
 * Client helpers for the `profiles` table (see
 * supabase/migrations/20260710140000_create_profiles.sql).
 *
 * A profile row carries the two independent access switches that gate the app:
 *   - shop_access   -> the order form (`/order`)
 *   - trials_access -> the clinical-trials dashboard (`/dashboard`)
 *
 * Reads are RLS-scoped: a signed-in user can read only their own row; admins
 * can read and update every row. All calls are forgiving when Supabase isn't
 * configured (local dev) so the UI keeps working.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const TABLE = "profiles";

const PROFILE_COLUMNS =
  "id, email, full_name, clinic_name, contact_name, phone, is_admin, " +
  "shop_access, trials_access, shop_requested, trials_requested, status, " +
  "decided_by, decided_at, enrollment_payload, spam_reason, created_at";

/**
 * Fetch the signed-in user's own profile row.
 *
 * @param {string} userId auth.users.id of the current user.
 * @returns {Promise<{ profile: (Object|null), error: (Error|null),
 *   unavailable: boolean }>} `unavailable` is true when the profiles table
 *   couldn't be reached at all (not configured / table missing), which callers
 *   use to fail open on the pre-existing trials surface without exposing the
 *   new shop surface.
 */
export async function fetchMyProfile(userId) {
  if (!userId) return { profile: null, error: null, unavailable: false };

  if (!isSupabaseConfigured) {
    return { profile: null, error: null, unavailable: true };
  }

  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select(PROFILE_COLUMNS)
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[Qentoros] Failed to load profile", error.message || error);
      return { profile: null, error, unavailable: true };
    }

    return { profile: data || null, error: null, unavailable: false };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[Qentoros] Profile fetch threw", err);
    return { profile: null, error: err, unavailable: true };
  }
}

/**
 * Admin: list every profile, newest first. RLS returns nothing for non-admins.
 */
export async function listProfiles() {
  if (!isSupabaseConfigured) return { profiles: [], error: null };
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select(PROFILE_COLUMNS)
      .order("created_at", { ascending: false });
    return { profiles: data || [], error: error || null };
  } catch (err) {
    return { profiles: [], error: err };
  }
}

/**
 * Admin: fetch a single profile by id. Used by the email-driven enrollment
 * decision page to show the clinic's details before the admin confirms. RLS
 * lets an admin read any row and returns nothing for non-admins.
 *
 * @param {string} id Profile id (auth user id).
 */
export async function fetchProfileById(id) {
  if (!id) return { profile: null, error: null };
  if (!isSupabaseConfigured) {
    return { profile: null, error: new Error("Supabase is not configured") };
  }
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select(PROFILE_COLUMNS)
      .eq("id", id)
      .maybeSingle();
    return { profile: data || null, error: error || null };
  } catch (err) {
    return { profile: null, error: err };
  }
}

/**
 * Admin: find the profile for an email address, or null when nobody has
 * enrolled under it (an "email-only" customer who only exists in orders and
 * the CRM). Matched case-insensitively — auth stores the address as typed at
 * signup, while orders and marketing captures lowercase it.
 *
 * @param {string} email
 */
export async function fetchProfileByEmail(email) {
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized) return { profile: null, error: null };
  if (!isSupabaseConfigured) {
    return { profile: null, error: new Error("Supabase is not configured") };
  }
  try {
    // ilike is case-insensitive but treats `_` and `%` as wildcards, and `_`
    // is legal in an address, so re-check the match exactly on the way out.
    const { data, error } = await supabase
      .from(TABLE)
      .select(PROFILE_COLUMNS)
      .ilike("email", normalized)
      .limit(5);
    if (error) return { profile: null, error };
    const exact = (data || []).find(
      (row) => (row.email || "").trim().toLowerCase() === normalized
    );
    return { profile: exact || null, error: null };
  } catch (err) {
    return { profile: null, error: err };
  }
}

/**
 * Admin: merge a patch into a profile's `enrollment_payload` (addresses, vet
 * credentials). The column is a single jsonb blob written once at signup, so
 * edits read the current value, merge, and write the whole object back — the
 * signed agreement fields inside it are never touched by the pane's editors.
 *
 * @param {string} id       Profile id.
 * @param {Object} current  The payload as currently loaded.
 * @param {Object} patch    Top-level keys to overwrite.
 */
export async function updateProfilePayload(id, current, patch) {
  if (!isSupabaseConfigured) {
    return { profile: null, error: new Error("Supabase is not configured") };
  }
  const next = { ...(current || {}), ...(patch || {}) };
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .update({ enrollment_payload: next })
      .eq("id", id)
      .select(PROFILE_COLUMNS)
      .maybeSingle();
    return { profile: data || null, error: error || null };
  } catch (err) {
    return { profile: null, error: err };
  }
}

/**
 * Admin: patch a profile's access flags / status. Returns the updated row.
 *
 * @param {string} id      Profile id (auth user id).
 * @param {Object} changes Columns to update (e.g. { shop_access: true }).
 * @param {string} [decidedBy] Email of the admin making the change; recorded on
 *   the row when a status decision is part of the update.
 */
export async function updateProfileAccess(id, changes, decidedBy) {
  if (!isSupabaseConfigured) {
    return { profile: null, error: new Error("Supabase is not configured") };
  }

  const patch = { ...changes };
  if (changes.status && decidedBy) {
    patch.decided_by = decidedBy;
    patch.decided_at = new Date().toISOString();
  }

  try {
    const { data, error } = await supabase
      .from(TABLE)
      .update(patch)
      .eq("id", id)
      .select(PROFILE_COLUMNS)
      .maybeSingle();
    return { profile: data || null, error: error || null };
  } catch (err) {
    return { profile: null, error: err };
  }
}

/**
 * Admin: delete a profile row. This removes the user from the access lists and
 * revokes shop + clinical-trials access. It does NOT delete the underlying
 * auth.users login (the client uses the anon key and can't reach the auth admin
 * API); a full account removal must be done from the Supabase dashboard.
 *
 * Deleting is gated by the `profiles_admin_delete` RLS policy. RLS silently
 * removes zero rows for non-admins, so we `.select()` the deleted row and treat
 * an empty result as a permission failure rather than a false success.
 *
 * @param {string} id Profile id (auth user id).
 */
export async function deleteProfile(id) {
  if (!isSupabaseConfigured) {
    return { error: new Error("Supabase is not configured") };
  }
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .delete()
      .eq("id", id)
      .select("id");
    if (error) return { error };
    if (!data || data.length === 0) {
      return {
        error: new Error(
          "The user could not be removed. You may not have permission, or the account no longer exists."
        ),
      };
    }
    return { error: null };
  } catch (err) {
    return { error: err };
  }
}

/**
 * Admin: create a new account directly, bypassing the public enrollment form
 * (which requires clinic info). Used to grandfather in existing customers and
 * to add employees. Delegates to the `admin-create-user` edge function because
 * creating an auth login needs the service-role key, which the frontend (anon
 * key) can't reach — the same reason deleteProfile can't remove the login.
 *
 * @param {Object} params
 * @param {string} params.email        Required. The new account's email.
 * @param {string} [params.fullName]
 * @param {string} [params.clinicName]
 * @param {string} [params.contactName]
 * @param {string} [params.phone]
 * @param {boolean} [params.shopAccess]  Grant shop access.
 * @param {boolean} [params.trialsAccess] Grant clinical-trials access.
 * @param {boolean} [params.isAdmin]      Make the account an admin.
 * @param {"temp"|"welcome"|"reset"} [params.credential] How the password is
 *   delivered. "temp" (default) returns a one-time password to show the admin;
 *   "welcome"/"reset" mean an email will be sent instead, so no temp password
 *   is returned (the caller sends the email after this resolves).
 * @returns {Promise<{ data: (Object|null), error: (Error|null) }>} On success
 *   `data` carries `{ userId, profile, tempPassword? }`.
 */
export async function createUser(params = {}) {
  if (!isSupabaseConfigured) {
    return { data: null, error: new Error("Supabase is not configured") };
  }
  try {
    const { data, error } = await supabase.functions.invoke(
      "admin-create-user",
      {
        body: {
          ...params,
          appUrl:
            typeof window !== "undefined" ? window.location.origin : undefined,
        },
      }
    );

    // functions.invoke surfaces a non-2xx as `error` with the Response in
    // error.context; read the function's JSON `error` for a useful message.
    if (error) {
      let message = error.message || "The account could not be created.";
      try {
        const payload = await error.context?.json?.();
        if (payload?.error) message = payload.error;
      } catch {
        /* fall back to error.message */
      }
      return { data: null, error: new Error(message) };
    }
    if (data?.error) return { data: null, error: new Error(data.error) };
    return { data: data || null, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

/**
 * Send a password-recovery email, landing the user on /reset-password. Shared by
 * the public "Forgot password" modal and the admin User access page, so both
 * paths produce the identical branded email.
 *
 * This goes through the `send-password-reset` edge function rather than
 * `supabase.auth.resetPasswordForEmail()`. The built-in call would hand the send
 * to Supabase Auth's default template — unstyled, sent by the Auth SMTP sender,
 * and invisible to the email_events delivery log. The function mints the same
 * recovery link server-side and mails it through Resend with the brand template.
 *
 * The Turnstile token is verified server-side there (the secret key never
 * reaches the browser), so it is required, not optional.
 *
 * The function deliberately reports success for an address that isn't
 * registered, so a caller can't use this to enumerate accounts.
 *
 * @param {string} email Address to send the reset link to.
 * @param {string} captchaToken Turnstile token from the widget. Single-use —
 *   reset the widget after each call.
 */
export async function sendPasswordReset(email, captchaToken) {
  if (!isSupabaseConfigured) {
    return { error: new Error("Supabase is not configured") };
  }
  try {
    const { data, error } = await supabase.functions.invoke(
      "send-password-reset",
      {
        body: {
          email,
          token: captchaToken,
          appUrl:
            typeof window !== "undefined" ? window.location.origin : undefined,
        },
      }
    );

    // functions.invoke surfaces a non-2xx as `error` with the Response in
    // error.context; read the function's JSON `error` for a useful message.
    if (error) {
      let message = error.message || "We couldn't send the reset email.";
      try {
        const payload = await error.context?.json?.();
        if (payload?.error) message = payload.error;
      } catch {
        /* fall back to error.message */
      }
      return { error: new Error(message) };
    }
    if (data?.error) return { error: new Error(data.error) };
    return { error: null };
  } catch (err) {
    return { error: err };
  }
}
